import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CursorArrowRaysIcon } from '@heroicons/react/24/solid';
import { useGetUser } from '../../hooks/api-hooks';

interface FaceCardProps {
  personaCandidateId: string;
  personaCandidateSwapUrl: string;
  personaSessionTargetUrl: string;
  similarity: number;
  allSimilarities: number[]; // New prop for all similarity scores
  previewNeoface: (personaSessionTargetUrl: string, candidateId: string, personaCandidateSwapUrl: string, similarity: number) => void;
  isFirstPersona: boolean;
}

const FaceCard: React.FC<FaceCardProps> = ({
   personaCandidateId,
   personaCandidateSwapUrl,
   previewNeoface,
   personaSessionTargetUrl,
   similarity,
   allSimilarities,
   isFirstPersona,
 }) => {
  const user = useGetUser();
  const navigate = useNavigate();

  const similarityStats = useMemo(() => {
    if (!allSimilarities?.length) {
      return {
        highThreshold: 0.8,
        mediumThreshold: 0.5,
        percentile: 0
      };
    }

    const sorted = [...allSimilarities].sort((a, b) => b - a);
    const position = sorted.indexOf(similarity);
    const percentile = (position / sorted.length) * 100;

    // Calculate thresholds based on the distribution
    const highThreshold = sorted[Math.floor(sorted.length * 0.15)] || 0.8; // top 15%
    const mediumThreshold = sorted[Math.floor(sorted.length * 0.5)] || 0.5; // top 50%

    return {
      highThreshold,
      mediumThreshold,
      percentile
    };
  }, [allSimilarities, similarity]);

  const getSimilarityConfig = (similarity: number) => {
    const { highThreshold, mediumThreshold, percentile } = similarityStats;

    if (similarity >= highThreshold) {
      return {
        label: 'Highly Similar',
        bgColor: 'bg-blue-50',
        textColor: 'text-blue-700',
        borderColor: 'border-blue-400',
      };
    } else if (similarity >= mediumThreshold) {
      return {
        label: 'Moderately Similar',
        bgColor: 'bg-indigo-50',
        textColor: 'text-indigo-700',
        borderColor: 'border-indigo-400',
      };
    } else {
      return {
        label: 'Less Similar',
        bgColor: 'bg-slate-50',
        textColor: 'text-slate-700',
        borderColor: 'border-slate-400',
      };
    }
  };

  const config = getSimilarityConfig(similarity);

  const handleSelect = () => {
    previewNeoface(
      personaSessionTargetUrl,
      personaCandidateId,
      personaCandidateSwapUrl,
      similarity,
    );
  };

  return (
    <div className='bg-white shadow rounded-lg overflow-hidden group'>
      {/* Image Container */}
      <div className='relative aspect-square overflow-hidden'>
        <img
          src={personaCandidateSwapUrl}
          alt='Neoface preview'
          className='h-full w-full object-cover transition-transform duration-300 group-hover:scale-105'
        />

        {/* Similarity Badge */}
        <div className='absolute top-3 right-3'>
          <span className={`
            inline-flex items-center rounded-full px-2.5 py-1 
            text-xs font-medium border
            ${config.bgColor} ${config.textColor} ${config.borderColor}
          `}>
            {config.label}
          </span>
        </div>

        {/* Hover Overlay */}
        <div
          onClick={handleSelect}
          className='absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 cursor-pointer'>
          <div
            className='absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
            <CursorArrowRaysIcon className='h-8 w-8 text-white drop-shadow-lg' />
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className='p-4'>
        <div className='flex justify-between items-center'>
          <button
            onClick={handleSelect}
            className='flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-500 transition-colors duration-150'
          >
            <CursorArrowRaysIcon className='h-4 w-4' />
            Preview & Select
          </button>
        </div>
      </div>
    </div>
  );
};

export default FaceCard;